import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface DropdownOption {
  value: string;
  label: string;
}

@customElement('multi-select-dropdown-field')
export class MultiSelectDropdownField extends LitElement {
  @property({ type: String }) name = '';
  @property({ type: String }) label = '';
  @property({ type: Array }) value: string[] = [];
  @property({ type: Array }) options: DropdownOption[] = [];
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error = '';
  @property({ type: Boolean }) touched = false;

  @state() private _isOpen = false;
  @state() private _searchTerm = '';

  static styles = css`
    :host {
      display: block;
    }
    .field-group {
      margin-bottom: 16px;
      position: relative;
    }
    label {
      display: block;
      font-weight: 500;
      color: #1e293b;
      font-size: 14px;
    }
    .required {
      color: #ef4444;
    }
    .dropdown-container {
      position: relative;
    }
    .dropdown-trigger {
      width: 100%;
      padding: 10px 36px 10px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 14px;
      background: white;
      cursor: pointer;
      text-align: left;
      transition: all 0.2s;
      position: relative;
    }
    .dropdown-trigger:hover {
      border-color: #94a3b8;
    }
    .dropdown-trigger.open {
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .dropdown-arrow {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      transition: transform 0.2s;
      pointer-events: none;
    }
    .dropdown-arrow.open {
      transform: translateY(-50%) rotate(180deg);
    }
    .selected-items {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;
    }
    .selected-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      background: #e0e7ff;
      color: #4338ca;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 500;
    }
    .placeholder {
      color: #94a3b8;
    }
    .dropdown-menu {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background: white;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      max-height: 300px;
      overflow-y: auto;
      z-index: 1000;
      animation: dropdownIn 0.2s ease-out;
    }
    @keyframes dropdownIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .search-box {
      padding: 8px;
      border-bottom: 1px solid #e2e8f0;
      position: sticky;
      top: 0;
      background: white;
      z-index: 1;
    }
    .search-input {
      width: 100%;
      padding: 8px;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      font-size: 14px;
      box-sizing: border-box;
      background-color: transparent;
      color: black;
    }
    .search-input:focus {
      outline: none;
      border-color: #4f46e5;
    }
    .option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      cursor: pointer;
      transition: background 0.2s;
    }
    .option:hover {
      background: #f1f5f9;
    }
    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      cursor: pointer;
      accent-color: #4f46e5;
    }
    .option-label {
      font-size: 14px;
      color: #1e293b;
      cursor: pointer;
      user-select: none;
    }
    .error-message {
      color: #ef4444;
      font-size: 12px;
      margin-top: 4px;
    }
  `;

  private _boundClickOutside?: (e: Event) => void;

  connectedCallback() {
    super.connectedCallback();
    this._boundClickOutside = this._handleClickOutside.bind(this);
    document.addEventListener('click', this._boundClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._boundClickOutside) {
      document.removeEventListener('click', this._boundClickOutside);
      this._boundClickOutside = undefined;
    }
  }

  private _handleClickOutside(e: Event) {
    const path = e.composedPath();
    if (!path.includes(this)) {
      this._isOpen = false;
    }
  }

  private _toggleDropdown() {
    this._isOpen = !this._isOpen;
    if (!this._isOpen) {
      this._searchTerm = '';
    }
  }

  private _handleToggle(optionValue: string) {
    if (this.value.includes(optionValue)) {
      this.value = this.value.filter((v) => v !== optionValue);
    } else {
      this.value = [...this.value, optionValue];
    }
    this._emitChange();
  }

  private _handleSearch(e: Event) {
    const input = e.target as HTMLInputElement;
    this._searchTerm = input.value.toLowerCase();
  }

  private _emitChange() {
    this.dispatchEvent(
      new CustomEvent('field-change', {
        detail: { name: this.name, value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _handleBlur() {
    this.dispatchEvent(
      new CustomEvent('field-blur', {
        detail: { name: this.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _getFilteredOptions() {
    if (!this._searchTerm) return this.options;
    return this.options.filter((opt) =>
      opt.label.toLowerCase().includes(this._searchTerm)
    );
  }

  private _getLabelForValue(value: string): string {
    const option = this.options.find((opt) => opt.value === value);
    return option?.label || value;
  }

  render() {
    const filteredOptions = this._getFilteredOptions();

    return html`
      <div class="field-group">
        <label>
          ${this.label}
          ${this.required ? html`<span class="required">*</span>` : ''}
        </label>
        <div class="dropdown-container">
          <button
            type="button"
            class="dropdown-trigger ${this._isOpen ? 'open' : ''}"
            @click=${this._toggleDropdown}
            @blur=${this._handleBlur}>
            ${this.value.length > 0
              ? html`
                  <div class="selected-items">
                    ${this.value.map(
                      (val) => html`
                        <span class="selected-chip">
                          ${this._getLabelForValue(val)}
                        </span>
                      `
                    )}
                  </div>
                `
              : html`<span class="placeholder">Select options...</span>`}
            <span class="dropdown-arrow ${this._isOpen ? 'open' : ''}">▼</span>
          </button>

          ${this._isOpen
            ? html`
                <div class="dropdown-menu">
                  <div class="search-box">
                    <input
                      type="text"
                      class="search-input"
                      placeholder="Search..."
                      .value=${this._searchTerm}
                      @input=${this._handleSearch}
                      @click=${(e: Event) => e.stopPropagation()} />
                  </div>
                  ${filteredOptions.map(
                    (option) => html`
                      <div
                        class="option"
                        @click=${() => this._handleToggle(option.value)}>
                        <input
                          type="checkbox"
                          id=${`${this.name}-${option.value}`}
                          .checked=${this.value.includes(option.value)}
                          @change=${() => this._handleToggle(option.value)} />
                        <label
                          class="option-label"
                          for=${`${this.name}-${option.value}`}>
                          ${option.label}
                        </label>
                      </div>
                    `
                  )}
                </div>
              `
            : ''}
        </div>
        ${this.error && this.touched
          ? html` <div class="error-message">${this.error}</div> `
          : ''}
      </div>
    `;
  }
}
